import { useEffect } from "react";
import { useSelector } from "react-redux";
import {
  getFtrReturnVisitorCookie,
  getReturnVisitorCookie,
  setReturnVisitorCookie,
  getWaitingPeriodValue,
} from "~/helpers/returnVisitorCookie";
import { ReduxState } from "~/types/redux";

/**
 * Custom hook to manage return visitor cookie based on tracking session, address state, and affiliate
 * Updates the cookie when address or affiliate changes, respecting waiting period rules
 */
export const useReturnVisitorCookie = () => {
  const {
    address,
    firebase,
    tracking: { affiliateKey, clientSessionId: trackingSessionId },
  } = useSelector((state: ReduxState) => state);

  const state = address?.install?.state || "";
  const controlNumber = address?.install?.controlNumber || "";

  useEffect(() => {
    if (!trackingSessionId || !firebase) return;

    const ftrCookie = getFtrReturnVisitorCookie();
    const rvCookie = getReturnVisitorCookie();
    const affiliateConfig = affiliateKey && JSON.parse(firebase.affiliateConfig._value)[affiliateKey];
    const affiliateName = affiliateConfig?.name;

    // Skip if there is already an RV cookie
    if (rvCookie?.shownReturnVisitorOffer) return;

    const rvoRules = firebase.rvoRules?._value ? JSON.parse(firebase.rvoRules._value) : null;
    const waitingPeriodValue = getWaitingPeriodValue(rvoRules, affiliateName, state);
    if (waitingPeriodValue === -1) return; // skip if no match found

    if (!ftrCookie) {
      // First time visitor so we only set 3MF if no waiting period
      const shownReturnVisitorOffer = waitingPeriodValue === 0;
      setReturnVisitorCookie(trackingSessionId, state, affiliateName, shownReturnVisitorOffer, controlNumber);
    } else {
      const { created } = ftrCookie;

      // Calculate the difference in days between today and created
      const today = new Date();
      const createdDate = new Date(created);
      const msPerDay = 24 * 60 * 60 * 1000;
      const dayDifference = Math.floor((today.getTime() - createdDate.getTime()) / msPerDay);
      const shownReturnVisitorOffer = waitingPeriodValue <= dayDifference;
      setReturnVisitorCookie(trackingSessionId, state, affiliateName, shownReturnVisitorOffer, controlNumber);
    }
  }, [trackingSessionId, state, controlNumber, firebase, affiliateKey]);
};
